import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "./ui/button";
import { isAddFriendDocument } from "../lib/addFriendHomeScreen";

// Layer 2 (safe/UI-only) of the installable-PWA work: a dismissible nudge that
// asks logged-in employees on a phone to install the portal to their home
// screen. Installing escapes Safari's ~7-day storage wipe, so it also cuts the
// weekly re-login complaints — no auth change. Pure UI: no data, no token, no
// network. Renders null for anyone not logged in (e.g. public event-link
// visitors), anyone on desktop, anyone already running the installed app, and
// anyone who tapped "Not now".

// Re-show the nudge at most once a week for people who haven't installed yet
// (Levi 2026-06-03): tapping "Not now" snoozes for 7 days instead of forever.
const SNOOZE_KEY = "kindred:installNudgeSnoozedUntil";
// Sticky "this device has run the installed app at least once" flag. Once we've
// ever seen standalone mode we never nudge again, even back in Safari — the
// browser tab itself has no API to ask "is this already on the home screen?",
// so this flag is how we remember across sessions.
const INSTALLED_KEY = "kindred:installSeen";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;
// Menu re-summon hook: any menu item can fire
// window.dispatchEvent(new Event(SUMMON_EVENT)) to re-open this nudge on demand.
const SUMMON_EVENT = "kindred:showInstallNudge";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent || "");
}

function isMobile() {
  return /android|iphone|ipad|ipod/i.test(window.navigator.userAgent || "");
}

export default function InstallAppNudge() {
  const token = useSelector((state) => state.auth?.token);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Android/Chrome fires beforeinstallprompt when the app is installable and
    // lets us trigger the native install dialog. iOS Safari never fires it, so
    // there we fall back to manual Share-sheet instructions (handled below).
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () =>
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  // Re-summon from the menu: fire window event SUMMON_EVENT to re-open the
  // nudge on demand (skips the 7-day snooze). No-op if already installed.
  useEffect(() => {
    const onSummon = () => {
      if (isStandalone()) return;
      if (isAddFriendDocument()) return; // this page's prompt installs Add Friend
      if (!isMobile()) return; // phones only — desktop has no install affordance
      setVisible(true);
    };
    window.addEventListener(SUMMON_EVENT, onSummon);
    return () => window.removeEventListener(SUMMON_EVENT, onSummon);
  }, []);

  useEffect(() => {
    if (!token) return; // logged-in employees only
    if (isAddFriendDocument()) return; // this page's prompt installs Add Friend
    if (isStandalone()) {
      // Running the installed app — remember it so we never nudge again, and
      // bail. This is a synchronous media-query/localStorage check: no network,
      // no measurable load cost.
      localStorage.setItem(INSTALLED_KEY, "1");
      return;
    }
    if (localStorage.getItem(INSTALLED_KEY) === "1") return; // installed before
    if (!isMobile()) return; // phones only
    const snoozedUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
    if (Date.now() < snoozedUntil) return; // within the 7-day snooze window
    // iOS: show the manual-instructions banner. Android: wait until we've
    // captured the install prompt so the "Install" button actually works.
    if (isIos() || deferredPrompt) setVisible(true);
  }, [token, deferredPrompt]);

  if (!visible) return null;

  const dismiss = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    setVisible(false);
  };

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    try {
      await deferredPrompt.userChoice;
    } finally {
      setDeferredPrompt(null);
      dismiss();
    }
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 px-3 pb-3 pointer-events-none">
      <div className="pointer-events-auto mx-auto flex max-w-md items-center gap-3 rounded-xl border border-white/10 bg-zinc-900/95 p-3 shadow-lg backdrop-blur">
        <img
          src="/logo192.png"
          alt=""
          className="h-9 w-9 rounded-md"
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-white">
            Install the Kindred app
          </p>
          <p className="text-xs text-zinc-400">
            {isIos()
              ? "Tap Share, then “Add to Home Screen” — opens full-screen and stays logged in."
              : "Add it to your home screen — opens full-screen and stays logged in."}
          </p>
        </div>
        {deferredPrompt ? (
          <Button size="sm" variant="kindred" onClick={install}>
            Install
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="link"
          onClick={dismiss}
          className="text-zinc-400"
          aria-label="Dismiss install prompt"
        >
          Not now
        </Button>
      </div>
    </div>
  );
}
